import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { Observable, from, of } from 'rxjs';
import { catchError, filter, first, switchMap } from 'rxjs/operators';
import {
    CheckEmailStatusResponse,
    CreateFromDraftRequest,
    CreateFromDraftResponse,
    PublishOrgAsTemplateRequest,
    PublishOrgAsTemplateResponse,
} from '@newgenus/common';

/**
 * Wrapper around the `api-orgCreation-createFromDraft` callable.
 *
 * The callable is the single sensitive op for the whole wizard:
 * - Reads `orgDrafts/{draftKey}` server-side.
 * - Verifies the caller owns the draft.
 * - Re-checks the 3-in-progress cap.
 * - Orchestrates the org-creation pipeline (org doc → orgFeatures → policies →
 *   CEO security group → `bulkAddParts` command-doc chunks → staff → orgUsers).
 *
 * Frontend role here is just: forward the draftKey + bearer token, receive the
 * new `orgKey` (or a structured error).
 *
 * Plan reference: §6 + §15.2.
 */
@Injectable({ providedIn: 'root' })
export class OrgCreationService {
    private http = inject(HttpClient);
    private auth = inject(AngularFireAuth);

    /**
     * Set by the host app at startup (same pattern as
     * {@link OrgBoardService.apiBaseUrl}) so dev / stage / production map to the
     * right Cloud Functions deployment.
     *
     * Example: `https://us-central1-newgenus-dev-19da7.cloudfunctions.net/api-`
     */
    public apiBaseUrl: string | undefined;

    public createFromDraft(draftKey: string): Observable<CreateFromDraftResponse> {
        const body: CreateFromDraftRequest = { draftKey };
        return from(this.auth.idToken).pipe(
            filter((t): t is string => !!t),
            first(),
            switchMap((token) => {
                const headers = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                };
                const url = this.apiBaseUrl
                    ? `${this.apiBaseUrl}orgCreationCreateFromDraft-default`
                    : `/api/orgCreationCreateFromDraft-default`;
                return this.http.post<CreateFromDraftResponse>(url, body, { headers });
            })
        );
    }

    /**
     * Resolve a single email against Firebase Auth via the reused
     * `api-invitationsCheckEmail-default` endpoint (plan §16.4, Step 4).
     *
     * Because the wizard's draft has no `orgKey` yet, we pass the `draftKey` —
     * `isUserAlreadyMember` and the pending-invitation query both safely
     * resolve to `false` against a non-existent org. The wizard only consumes
     * the `existsInAuth` / `displayName` fields.
     *
     * Errors fold into a safe "unknown" response so the staff row UI can stay
     * responsive even when the network is flaky.
     */
    /**
     * Snapshot an existing org into `orgTemplates/{templateKey}` via the
     * `api-orgCreationPublishOrgAsTemplate-default` callable (P4.c). Caller
     * must be an owner of `orgKey`; the server re-checks.
     */
    public publishOrgAsTemplate(request: PublishOrgAsTemplateRequest): Observable<PublishOrgAsTemplateResponse> {
        return from(this.auth.idToken).pipe(
            filter((t): t is string => !!t),
            first(),
            switchMap((token) => {
                const headers = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                };
                const url = this.apiBaseUrl
                    ? `${this.apiBaseUrl}orgCreationPublishOrgAsTemplate-default`
                    : `/api/orgCreationPublishOrgAsTemplate-default`;
                return this.http.post<PublishOrgAsTemplateResponse>(url, request, { headers });
            })
        );
    }

    public checkEmail(email: string, draftKey: string): Observable<CheckEmailStatusResponse> {
        return from(this.auth.idToken).pipe(
            filter((t): t is string => !!t),
            first(),
            switchMap((token) => {
                const headers = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                };
                const url = this.apiBaseUrl
                    ? `${this.apiBaseUrl}invitationsCheckEmail-default`
                    : `/api/invitationsCheckEmail-default`;
                return this.http.post<CheckEmailStatusResponse>(url, { email, orgKey: draftKey }, { headers });
            }),
            catchError((err) => {
                console.error('OrgCreationService > checkEmail > error:', err);
                return of({ existsInAuth: false, existsInOrg: false } as CheckEmailStatusResponse);
            })
        );
    }
}
